import { useTranslation } from "@redotech/redo-i18n";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { OptionalTooltip } from "@redotech/redo-web/tooltip/optional-tooltip";
import { OverflowTooltip } from "@redotech/redo-web/tooltip/overflow-tooltip";
import { memo, useState } from "react";
import type { SummaryLine } from "./rule-node-summary";

/**
 * Lines a node shows before the rest collapse into "+N more". Capping logical
 * lines rather than CSS-clamping keeps N exact and the affordance visible.
 */
const VISIBLE_LINES = 3;

const lineText = (line: SummaryLine) =>
  line.map((segment) => segment.text).join(" ");

/**
 * Weighted rather than badged: a rule stacks several lines, where badges on
 * every field and value would read as noise instead of structure.
 *
 * One truncating block with inline spans, not a flex row. A row can only
 * ellipsize an individual child, so the last segment ends up with a sliver too
 * narrow to fit the "..." and the browser hard-clips it mid-glyph instead.
 */
function RuleNodeSummaryLine({ line }: { line: SummaryLine }) {
  const [overflowRef, setOverflowRef] = useState<HTMLElement | null>(null);

  return (
    <OverflowTooltip
      overflowRef={overflowRef}
      tooltipProps={{ title: lineText(line) }}
    >
      <Text
        fontSize="xs"
        overflow="hidden"
        ref={setOverflowRef}
        textOverflow="ellipsis"
        w="full"
        whiteSpace="nowrap"
      >
        {line.map((segment, index) => (
          <Text
            as="span"
            fontWeight={segment.kind === "text" ? undefined : "semibold"}
            key={index}
          >
            {index === 0 ? segment.text : ` ${segment.text}`}
          </Text>
        ))}
      </Text>
    </OverflowTooltip>
  );
}

/**
 * A rule summary trimmed to a few lines, with the remainder counted in a
 * "+N more" hint and spelled out in full on hover. The hover copy wraps rather
 * than reusing the node's lines, which would truncate what it exists to reveal.
 */
export const RuleNodeSummaryText = memo(function RuleNodeSummaryText({
  summary,
}: {
  summary: SummaryLine[];
}) {
  const { t } = useTranslation();
  const hidden = Math.max(0, summary.length - VISIBLE_LINES);

  return (
    <OptionalTooltip
      meetsCondition={hidden > 0}
      tooltipProps={{
        title: (
          <Flex dir="column" gap="none">
            {summary.map((line, index) => (
              <Text fontSize="xs" key={index}>
                {lineText(line)}
              </Text>
            ))}
          </Flex>
        ),
      }}
    >
      <Flex dir="column" gap="none" w="full">
        {summary.slice(0, VISIBLE_LINES).map((line, index) => (
          <RuleNodeSummaryLine key={index} line={line} />
        ))}
        {hidden > 0 && (
          <Text fontSize="xs" textColor="secondary">
            {t("+{{count}} more", { count: hidden })}
          </Text>
        )}
      </Flex>
    </OptionalTooltip>
  );
});
